import { entityStore } from '~/utility/BinSentry-ui-utility';
import { BnModalMixin } from '../../common/bn-modal-mixin';
import { html, LitEntity, renderIf } from '../../utility/lit-entity';
import '../../common/bn-flash';

class FasIntegrationModal extends BnModalMixin(LitEntity) {
  constructor() {
    super();
    this.id = 'fas-integration-modal';
  }

  static get properties() {
    return {
      applyingCredentials: { type: Boolean, attribute: false },
    };
  }

  renderContents() {
    return html`
      <style>
        bn-modal#fas-integration-modal bn-flash {
          padding: 0 10px;
        }
        bn-modal#mtech-integration-modal .description {
          padding: 0 20px 10px 20px;
        }
        bn-modal#fas-integration-modal .status {
          font-weight: bold;
        }
        bn-modal#fas-integration-modal .action-surface {
          position: relative;
        }
        bn-modal#fas-integration-modal .hidden {
          visibility: hidden;
        }
        bn-modal#fas-integration-modal .load-spinner {
          position: absolute;
          width: 100%;
        }
      </style>
      <h3 slot="title">${this.title}</h3>
      <div class="description">
        <p>
          The FAS integration allows forwarding orders and bin inventories to FAS.
        </p>
        <p>
          FAS credentials must be provided for the integration to work.
        </p>
        <p>
          Once the credentials are configured, add the following tag to bins in order to forward bin level data and orders to FAS:
        </p>
        <ul>
          <li>
            _fas.binId - The FAS bin ID for the selected bin
          </li>
        </ul>
      </div>
      <bn-flash></bn-flash>

      <bn-surface>
        <div>
          <label>Status:</label>
          <span class="status">${this.entity.properties.credentialsConfigured ? 'Enabled' : 'Disabled'}</span>
        </div>
      </bn-surface>

      ${renderIf(this.entity.hasAction('set-fas-credentials'), () => html`
        <bn-surface class="action-surface">
          ${renderIf(this.applyingCredentials, () => html`<div class="load-spinner"><bn-loading><p>Saving credentials...</p></bn-loading></div>`)}
          <bn-action
            class="${this.applyingCredentials ? 'hidden' : ''}"
            .href="${this.href}"
            actionname="set-fas-credentials"
            @submit="${this._onCredentialsSubmit}"
            @response="${this._onCredentialsResponse}"
            @error="${this._onCredentialsError}"
            suppressflash
          >
            <h3 slot="header">
              ${this.entity.properties.credentialsConfigured ? 'Update' : 'Set'} credentials
            </h3>
          </bn-action>
        </bn-surface>
      `)}

      ${renderIf(this.entity.hasAction('update-fas-configuration'), () => html`
        <bn-surface class="action-surface">
          <bn-action
            .href="${this.href}"
            actionname="update-fas-configuration"
            @response="${this._onConfigurationResponse}"
            @error="${this._onConfigurationError}"
            suppressflash
          >
            <h3 slot="header">Update configuration</h3>
          </bn-action>
        </bn-surface>
      `)}

      ${renderIf(this.entity.hasAction('delete-fas-configuration'), () => html`
        <bn-surface>
          <bn-action
            .href="${this.href}"
            actionname="delete-fas-configuration"
            submittext="Delete fas configuration"
            buttonstyle='outlinedError'
            .preSubmit="${this._confirmDeletingInstallerCredentials}"
            @response="${() => this._onResponse('FAS integration has been disabled.')}"
            @error="${(e) => this._onError(e, 'Could not delete fas credentials.')}"
          >
            <h3 slot="header">Disable FAS integration</h3>
          </bn-action>
        </bn-surface>
      `)}
    `;
  }

  async _onCredentialsSubmit() {
    this.applyingCredentials = true;
  }

  async _onCredentialsResponse() {
    this.applyingCredentials = false;
    return this._onResponse('Credentials have been configured. FAS integration is enabled.');
  }

  async _onCredentialsError(e) {
    this.applyingCredentials = false;
    return this._onError(e, 'Could not set FAS credentials.');
  }

  async _onConfigurationResponse() {
    return this._onResponse('Configuration updated');
  }

  async _onConfigurationError(e) {
    return this._onError(e, 'Could not set FAS configuration.');
  }

  async _onResponse(successText) {
    this._getFlash().clear();
    await entityStore.fetchWait(this.getHref('self'));
    this._getFlash().success(successText);
  }

  async _onError(event, errorText) {
    this._getFlash().clear();
    this._getFlash().critical(html`${errorText}<div><code>${event.error.message}</code></div>`);
  }

  _getFlash() {
    return this.renderRoot.querySelector('bn-flash');
  }

  _confirmDeletingInstallerCredentials() {
    const confirmMessage = 'Are you sure you want to delete FAS credentials? This will disable FAS integration.';
    return window.confirm(confirmMessage);
  }
}

window.customElements.define('fas-integration-modal', FasIntegrationModal);
